import { InstrumentType, StringsTuning, TablatureSingleChord } from "../components/instruments/InstrumentTypes.model";
import { chooseBestStringInstrumentTab } from "../functions/chooseBestStringInstrumentTab";
import { ChordInScaleEdit } from "../models/wasm.model";

/**
 * Wrapper to hold stringed instrument tablature for multiple instruments.
 *
 * Functions below are CRUD - create read update delete - operations on this wrapper.
 */
export type TabsAll = Record<InstrumentType, Array<TablatureSingleChord>>;

export type SingleTabAllInstruments = Record<InstrumentType, TablatureSingleChord>;

export type InstrumentSettingsMap = Record<InstrumentType, { stringsTuning: StringsTuning; capoPosition: number }>;

/*
 * Simple insert/delete/replace functions
 */
export function replaceTabForInstrument(
  tabs: TabsAll,
  instrumentType: InstrumentType,
  newTab: TablatureSingleChord,
  index: number
): TabsAll {
  return { ...tabs, [instrumentType]: tabs[instrumentType].toSpliced(index, 1, newTab) };
}
export function replaceAllTabsForInstrument(tabs: TabsAll, type: InstrumentType, newTabs: Array<TablatureSingleChord>): TabsAll {
  return {
    ...tabs,
    [type]: newTabs,
  };
}
export function deleteTabsForChord(tabs: TabsAll, index: number): TabsAll {
  const newTabs = { ...tabs };
  Object.keys(newTabs).forEach((k) => {
    const key = k as InstrumentType;
    newTabs[key] = newTabs[key].toSpliced(index, 1);
  });
  return newTabs;
}
function addTabsForChord(tabs: TabsAll, newTab: SingleTabAllInstruments): TabsAll {
  const tabsAll = { ...tabs };
  Object.keys(newTab).forEach((k) => {
    const key = k as InstrumentType;
    tabsAll[key] = tabsAll[key].concat([newTab[key]]);
  });
  return tabsAll;
}
function replaceTabsForChord(tabs: TabsAll, newTab: SingleTabAllInstruments, index: number): TabsAll {
  const tabsAll = { ...tabs };
  Object.keys(newTab).forEach((k) => {
    const key = k as InstrumentType;
    tabsAll[key] = tabsAll[key].toSpliced(index, 1, newTab[key]);
  });
  return tabsAll;
}

/*
 * Builder functions which attempt at tab creation, as well as CRUD'ing it into place.
 */

export function createAndAddTabForEachInstrument(tabsAll: TabsAll, settings: InstrumentSettingsMap, chord: ChordInScaleEdit): TabsAll {
  let newTabs: Partial<SingleTabAllInstruments> = {};
  Object.keys(settings).forEach((is) => {
    const instrumentType = is as InstrumentType;
    const { capoPosition, stringsTuning } = settings[instrumentType];
    newTabs[instrumentType] = chooseBestStringInstrumentTab({ chord, capoPosition, stringsTuning });
  });
  return addTabsForChord(tabsAll, newTabs as SingleTabAllInstruments);
}

export function updateTabsForInstrumentSettingsChange(
  tabsAll: TabsAll,
  chords: Array<ChordInScaleEdit>,
  instrumentType: InstrumentType,
  settings: InstrumentSettingsMap
): TabsAll {
  const { capoPosition, stringsTuning } = settings[instrumentType];
  const newTabs = chords.map((chord) => {
    return chooseBestStringInstrumentTab({ chord, capoPosition, stringsTuning });
  });
  return replaceAllTabsForInstrument(tabsAll, instrumentType, newTabs);
}

export function updateTabsForSingleChordChange(
  tabsAll: TabsAll,
  index: number,
  chord: ChordInScaleEdit,
  settings: InstrumentSettingsMap
): TabsAll {
  let newTabs: Partial<SingleTabAllInstruments> = {};
  Object.keys(settings).forEach((is) => {
    const instrumentType = is as InstrumentType;
    const { capoPosition, stringsTuning } = settings[instrumentType];
    newTabs[instrumentType] = chooseBestStringInstrumentTab({ chord, capoPosition, stringsTuning });
  });
  return replaceTabsForChord(tabsAll, newTabs as SingleTabAllInstruments, index);
}
